export default function ReportList({ reports, loading, onViewReport, onDeleteReport, onViewSources }) {
  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block h-8 w-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mb-4"></div>
        <p className="text-slate-600 text-sm">Loading reports...</p>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="text-5xl mb-4">📊</div>
        <h3 className="text-lg font-medium text-slate-900 mb-2">No Reports Available</h3>
        <p className="text-slate-600 text-sm max-w-md mx-auto">
          Generate your first intelligence report to begin building your market analysis library
        </p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-slate-900">
          Reports Library
        </h2>
        <span className="text-sm text-slate-500">
          {reports.length} {reports.length === 1 ? 'report' : 'reports'}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Report Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Region
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Generated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {reports.map((report) => (
              <tr key={report.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-slate-900 mb-1">
                    {report.title}
                  </div>
                  <div className="text-xs text-slate-500">
                    🔑 {report.keywords}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">
                    {report.industry_name || report.industry}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {report.region}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={report.status} />
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {formatDate(report.created_at || report.createdAt)}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewSources(report)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                      title="View news sources used for this report"
                    >
                      Sources
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={() => onViewReport(report)}
                      className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors"
                    >
                      View
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this report?')) {
                          onDeleteReport(report.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const statusConfig = {
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Pending' },
    generating: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Generating' },
    completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Completed' },
    failed: { bg: 'bg-red-50', text: 'text-red-700', label: 'Failed' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
